import { makeStyles } from '@material-ui/core/styles';
import classnames from 'classnames';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import _fill from 'lodash/fill';
import { color } from '../../../app/theme';
import { ModalType, PlatformType } from '../../../app/types';
import IconEdit from '../../../assets/images/Icons/IconEdit';
import IcBrowse from '../../../assets/images/svg/ic-browse.svg';
import { setWhatsappTemplate } from '../../../features/mainSlice';
import { openModal } from '../../../features/modalSlice';
import Dropdown from '../Dropdown';
import TextFieldCardCounter from '../TextFieldCardCounter';
import Tip from '../Tip';
import Icons from './Icons';
import Template from './Template';
import TemplateDropdown from './TemplateDropdown';

const useStyles = makeStyles(
  () => ({
    root: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 12,
    },
    dropdown: {
      flex: '0 0 auto',
    },
    contentContainer: {
      position: 'relative',
      width: '100%',
    },
    content: {
      cursor: 'pointer',
      width: '100%',
      height: 44,
      fontSize: 15,
      color: color.$3,
      lineHeight: 1,
      borderRadius: 24,
      border: `solid 1px ${color.$10}`,
      backgroundColor: color.$7,
      padding: '15px 48px 14px 24px',
      resize: 'none',
      outline: 'none',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      '&::placeholder': {
        fontSize: 15,
        color: color.$3,
      },
    },
    error: { border: `solid 1px ${color.$17}` },
    icon: {
      cursor: 'pointer',
      position: 'absolute',
      top: 10,
      right: 16,
    },
    paramBlock: {
      marginTop: 8,
    },
    paramTitle: {
      fontSize: '15px',
      lineHeight: '21px',
      color: color.$3,
      marginBottom: 8,
    },
    UploadFileInput: {
      cursor: 'pointer',
      width: '100%',
      height: 44,
      fontSize: 15,
      color: color.$3,
      lineHeight: 1,
      borderRadius: 24,
      border: 'solid 1px #bdbdbd',
      backgroundColor: '#ffffff',
      padding: '15px 48px 14px 24px',
      resize: 'none',
      outline: 'none',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      '&::placeholder': {
        fontSize: 15,
        color: color.$3,
      },
    },
    UploadFileIcon: {
      cursor: 'pointer',
      position: 'absolute',
      top: 10,
      right: 16,
    },
  }),
  {
    name: 'WhatsappTemplate',
  },
);

function countPlaceholders(str) {
  // 正则表达式匹配{{数字}}
  const regex = /\{\{\d+\}\}/g;
  // 使用match方法找到所有匹配项
  const matches = str.match(regex);
  // 如果有匹配项，返回数量，否则返回0
  return matches ? matches.length : 0;
}

export const getParams = template => {
  const params = {};
  const { contentType } = template;
  const { templateType } = template?.data || {};

  switch (contentType) {
    case 'text/plain':
      const placeholderCount = countPlaceholders(template?.data?.content || '');
      if (placeholderCount) {
        params.parameters = placeholderCount
          ? _fill(Array(placeholderCount), {
              type: 'customer',
              value: 'originalDisplayName',
            })
          : [];
      }
      break;
    case 'application/x-image':
    case 'application/x-audio':
    case 'application/x-video':
    case 'application/x-file':
      if (template?.data?.url) {
        params.mediaParameters = [
          {
            type: 'url',
            value: template?.data?.url,
          },
        ];
      }
      break;
    default:
      break;
  }
  if (templateType === 'card' || templateType === 'confirm') {
    const element = template?.data?.elements?.[0];
    if (element?.imageUrl) {
      params.mediaParameters = [
        {
          type: 'url',
          value: element?.imageUrl,
        },
      ];
    }

    if (element?.buttons?.find(button => button?.type === 'url')) {
      params.buttonParameters = [];
      element?.buttons?.forEach(button => {
        if (button.type === 'url') {
          params.buttonParameters.push({
            type: button.type,
            value: (button || {}).data,
          });
        }
      });
    }
  }

  return params;
};

// 替換 elements 中的 buttons 的 value
function replaceButtonUrlsByWhatsappTemplateParams(originalData) {
  const newData = { ...originalData };
  let buttonIndex = 0;

  newData.elements = newData.elements.map(element => {
    const newElement = { ...element };
    newElement.buttons = element.buttons.map(button => {
      if (
        button.type === 'url' &&
        buttonIndex < newData?.buttonParameters.length
      ) {
        const newButton = {
          ...button,
          data: newData?.buttonParameters[buttonIndex].value,
        };
        buttonIndex += 1;
        return newButton;
      }
      return button;
    });
    return newElement;
  });

  return newData;
}

function UploadFile({
  className,
  disabled,
  text,
  accept,
  onChange,
  isCardImage,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { objectId: orgId } = useSelector(store => store.organization);
  const classes = useStyles();

  function handleOpenUploadFileDialog() {
    if (!disabled) {
      dispatch(
        openModal({
          modalType: ModalType.UPLOAD_DIALOG,
          modalProps: {
            orgId,
            platformType: PlatformType.WHATSAPP,
            accept,
            isCardImage,
            isWhatsappTemplate: true,
            onUploaded: onChange,
          },
        }),
      );
    }
  }

  return (
    <div
      className={classnames(className, classes.contentContainer)}
      style={{ margin: 0 }}
      onClick={handleOpenUploadFileDialog}
    >
      <input
        className={classes.UploadFileInput}
        value={text}
        placeholder={t('templates.placeholder-file')}
        readOnly
      />
      <div className={classes.UploadFileIcon}>
        <img src={IcBrowse} alt="Select File" />
      </div>
    </div>
  );
}

function WhatsappTemplate({
  template,
  onChangeType,
  onChange,
  onDelete,
  platformType,
  onUp,
  onDown,
  messageLength,
  messageIndex,
  isError,
}) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { waTemplateId, data } = template;

  const userOptionsGetType = [
    {
      value: 'originalDisplayName',
      text: t('templates.whatsappTemplate.originalDisplayName'),
    },
    { value: 'displayName', text: t('templates.whatsappTemplate.displayName') },
    { value: 'gender', text: t('templates.whatsappTemplate.gender') },
    { value: 'birthday', text: t('templates.whatsappTemplate.birthday') },
    { value: 'cellPhone', text: t('templates.whatsappTemplate.cellPhone') },
    { value: 'email', text: t('templates.whatsappTemplate.email') },
    { value: 'nation', text: t('templates.whatsappTemplate.nation') },
    { value: 'location', text: t('templates.whatsappTemplate.location') },
    { value: 'address', text: t('templates.whatsappTemplate.address') },
    { value: 'about', text: t('templates.whatsappTemplate.about') },
    { value: 'language', text: t('templates.whatsappTemplate.language') },
    { value: 'objectId', text: t('templates.whatsappTemplate.objectId') },
    { value: 'customerId', text: t('templates.whatsappTemplate.customerId') },
    {
      value: 'customField1',
      text: t('templates.whatsappTemplate.customField1'),
    },
    {
      value: 'customField2',
      text: t('templates.whatsappTemplate.customField2'),
    },
    {
      value: 'customField3',
      text: t('templates.whatsappTemplate.customField3'),
    },
    {
      value: 'hiddenField1',
      text: t('templates.whatsappTemplate.hiddenField1'),
    },
    {
      value: 'hiddenField2',
      text: t('templates.whatsappTemplate.hiddenField2'),
    },
  ];

  const whatsappTemplateType = [
    { value: 'customer', text: t('templates.whatsappTemplate.customer') },
    { value: 'custom', text: t('templates.whatsappTemplate.custom') },
  ];

  const hasParams = useMemo(() => {
    if (!data) {
      return false;
    }

    return (
      (data.parameters && data.parameters.length > 0) ||
      (data.mediaParameters && data.mediaParameters.length > 0) ||
      (data.buttonParameters && data.buttonParameters.length > 0)
    );
  }, [data]);

  const paramsComponent = useMemo(() => {
    if (!hasParams) {
      return null;
    }

    const { parameters, mediaParameters, buttonParameters } = data || {};
    const components = [
      <div
        key="paramsComponent-title"
        className={classnames(classes.paramTitle, classes.paramBlock)}
      >
        {t('templates.whatsappTemplate.paramTitle')}
      </div>,
    ];

    if (parameters && parameters.length > 0) {
      parameters.forEach((param, index) => {
        components.push(
          <div
            key={`${waTemplateId}-parameters-${index}`}
            className={classes.paramBlock}
          >
            <div className={classes.paramTitle}>
              {t('templates.whatsappTemplate.param')}
              {index + 1}
            </div>
            <div className={classes.customParam}>
              <Dropdown
                style={{ marginBottom: 8 }}
                value={param.type}
                placeholder=""
                onChange={v => {
                  onChange({
                    ...template,
                    data: {
                      ...template.data,
                      parameters: parameters.map((p, i) => {
                        if (i === index) {
                          if (v === 'customer') {
                            return {
                              ...p,
                              type: v,
                              value: userOptionsGetType[0].value,
                            };
                          }
                          return {
                            ...p,
                            type: v,
                            value: '',
                          };
                        }
                        return p;
                      }),
                    },
                  });
                }}
                menuItems={whatsappTemplateType}
              />
              {param.type === 'customer' ? (
                <Dropdown
                  value={param.value}
                  placeholder=""
                  onChange={v => {
                    onChange({
                      ...template,
                      data: {
                        ...template.data,
                        parameters: parameters.map((p, i) =>
                          i === index
                            ? {
                                ...p,
                                value: v,
                              }
                            : p,
                        ),
                      },
                    });
                  }}
                  menuItems={userOptionsGetType}
                />
              ) : (
                <TextFieldCardCounter
                  type="text"
                  maxCount={0}
                  placeholder={t('templates.whatsappTemplate.paramPlaceholder')}
                  isValid
                  value={param.value}
                  onChange={value => {
                    onChange({
                      ...template,
                      data: {
                        ...template.data,
                        parameters: parameters.map((p, i) =>
                          i === index
                            ? {
                                ...p,
                                value,
                              }
                            : p,
                        ),
                      },
                    });
                  }}
                  status={isError && !param.value ? 'error' : 'default'}
                />
              )}
            </div>
          </div>,
        );
      });
    }

    if (mediaParameters && mediaParameters.length > 0) {
      const { contentType } = template || {};
      const { templateType } = template?.data || {};

      if (templateType === 'card') {
        components.push(
          <div
            key={`${waTemplateId}-mediaParameters`}
            className={classes.paramBlock}
          >
            <div className={classes.paramTitle}>
              {t('templates.whatsappTemplate.image')}
            </div>
            <UploadFile
              isCardImage
              accept=".png,.jpg,.jpeg"
              text={t('templates.whatsappTemplate.imagePlaceholder')}
              onChange={res => {
                onChange({
                  ...template,
                  data: {
                    ...template.data,
                    elements: template?.data?.elements?.map(e => ({
                      ...e,
                      imageUrl: res?.data?.url,
                    })),
                    mediaParameters: [
                      {
                        type: 'url',
                        value: res?.data?.url,
                      },
                    ],
                  },
                });
              }}
            />
          </div>,
        );
      }

      switch (contentType) {
        case 'application/x-image':
          components.push(
            <div
              key={`${waTemplateId}-mediaParameters`}
              className={classes.paramBlock}
            >
              <div className={classes.paramTitle}>
                {t('templates.whatsappTemplate.image')}
              </div>
              <UploadFile
                accept=".png,.jpg,.jpeg"
                text={t('templates.whatsappTemplate.imagePlaceholder')}
                onChange={res => {
                  onChange({
                    ...template,
                    data: {
                      ...template.data,
                      url: res?.data?.url,
                      name: res?.data?.name,
                      mediaParameters: [
                        {
                          type: 'url',
                          value: res?.data?.url,
                        },
                      ],
                    },
                  });
                }}
              />
              <Tip
                style={{ marginTop: 8 }}
                text={t('templates.whatsappTemplate.fileUploadTip')}
              />
            </div>,
          );
          break;
        case 'application/x-audio':
          components.push(
            <div
              key={`${waTemplateId}-mediaParameters`}
              className={classes.paramBlock}
            >
              <div className={classes.paramTitle}>
                {t('templates.whatsappTemplate.audio')}
              </div>
              <UploadFile
                accept=".mp3,.aac,.amr,.opus"
                text={t('templates.whatsappTemplate.audioPlaceholder')}
                onChange={res => {
                  onChange({
                    ...template,
                    data: {
                      ...template.data,
                      url: res?.data?.url,
                      name: res?.data?.name,
                      mediaParameters: [
                        {
                          type: 'url',
                          value: res?.data?.url,
                        },
                      ],
                    },
                  });
                }}
              />
              <Tip
                style={{ marginTop: 8 }}
                text={t('templates.whatsappTemplate.fileUploadTip')}
              />
            </div>,
          );
          break;
        case 'application/x-video':
          components.push(
            <div
              key={`${waTemplateId}-mediaParameters`}
              className={classes.paramBlock}
            >
              <div className={classes.paramTitle}>
                {t('templates.whatsappTemplate.video')}
              </div>
              <UploadFile
                accept=".mp4,.3gpp"
                text={t('templates.whatsappTemplate.videoPlaceholder')}
                onChange={res => {
                  onChange({
                    ...template,
                    data: {
                      ...template.data,
                      url: res?.data?.url,
                      name: res?.data?.name,
                      mediaParameters: [
                        {
                          type: 'url',
                          value: res?.data?.url,
                        },
                      ],
                    },
                  });
                }}
              />
              <Tip
                style={{ marginTop: 8 }}
                text={t('templates.whatsappTemplate.fileUploadTip')}
              />
            </div>,
          );
          break;
        case 'application/x-file':
          components.push(
            <div
              key={`${waTemplateId}-mediaParameters`}
              className={classes.paramBlock}
            >
              <div className={classes.paramTitle}>
                {t('templates.whatsappTemplate.file')}
              </div>
              <UploadFile
                accept=".pdf"
                text={t('templates.whatsappTemplate.filePlaceholder')}
                onChange={res => {
                  onChange({
                    ...template,
                    data: {
                      ...template.data,
                      url: res?.data?.url,
                      name: res?.data?.name,
                      mediaParameters: [
                        {
                          type: 'url',
                          value: res?.data?.url,
                        },
                      ],
                    },
                  });
                }}
              />
              <Tip
                style={{ marginTop: 8 }}
                text={t('templates.whatsappTemplate.fileUploadTip')}
              />
            </div>,
          );
          break;
        default:
          break;
      }
    }

    if (buttonParameters && buttonParameters.length > 0) {
      const element = data?.elements?.[0] || {};

      const allButtons = element?.buttons?.filter(
        button => button.type === 'url',
      );

      buttonParameters.forEach((button, index) => {
        if (button.type === 'url') {
          components.push(
            <div
              key={`${waTemplateId}-buttonParameters-${index}`}
              className={classes.paramBlock}
            >
              <div className={classes.paramTitle}>
                {((allButtons || [])[index] || {}).title}
                {t('templates.whatsappTemplate.urlButton')}
              </div>
              <TextFieldCardCounter
                type="text"
                maxCount={0}
                placeholder={t(
                  'templates.whatsappTemplate.urlButtonPlaceholder',
                )}
                isValid
                value={button.value}
                onChange={value => {
                  const newData = replaceButtonUrlsByWhatsappTemplateParams({
                    ...template.data,
                    buttonParameters: buttonParameters.map((b, i) =>
                      i === index
                        ? {
                            ...b,
                            value,
                          }
                        : b,
                    ),
                  });

                  onChange({
                    ...template,
                    data: newData,
                  });
                }}
                status={isError && !button.value ? 'error' : 'default'}
              />
            </div>,
          );
        }
      });
    }

    return <div>{components}</div>;
  }, [template, isError]);

  function handleClick() {
    dispatch(
      setWhatsappTemplate({
        isShow: true,
        messageIndex,
      }),
    );
  }

  useEffect(() => {
    if (waTemplateId) {
      let newData = {};

      if (hasParams) {
        newData = { ...data };
        const mediaUrl = data?.mediaParameters?.[0]?.value;

        // 如果有參數，則取出參數替換掉預覽的圖片或影片連結
        // 如果沒參數代表之前尚未存取過參數，需設定預設參數值
        if (mediaUrl) {
          const { contentType } = template;
          const { templateType } = template?.data || {};

          switch (contentType) {
            case 'application/x-image':
            case 'application/x-audio':
            case 'application/x-video':
            case 'application/x-file':
              if (mediaUrl) {
                newData.url = mediaUrl;
                newData.name = mediaUrl.substring(
                  mediaUrl.lastIndexOf('/') + 1,
                );
              }
              break;
            default:
              break;
          }
          if (templateType === 'card') {
            const element = template?.data?.elements?.[0];
            newData.elements = [{ ...element, imageUrl: mediaUrl }];
          }
        }
      } else {
        newData = { ...data, ...getParams(template) };
      }

      onChange({
        data: newData,
      });
    }
  }, []);

  return (
    <Template>
      <div className={classes.root}>
        <TemplateDropdown
          className={classes.dropdown}
          platformType={platformType}
          messageTemplatetype="whatsappTemplate"
          onChange={onChangeType}
        />
        <Icons
          index={template.index}
          onUp={onUp}
          onDown={onDown}
          onDelete={onDelete}
          messageLength={messageLength}
        />
      </div>
      <div className={classes.contentContainer} onClick={handleClick}>
        <input
          className={classnames(classes.content, {
            [classes.error]: isError && !waTemplateId,
          })}
          value={template.name ?? ''}
          placeholder={t('templates.edit-whatsapp-template')}
          readOnly
        />
        <div className={classes.icon}>
          <IconEdit
            className={classes.iconSvg}
            id={`edit-${uuidv4()}`}
            color={color.$10}
            hoverColor={color.$10}
          />
        </div>
      </div>
      {paramsComponent}
    </Template>
  );
}

export default WhatsappTemplate;
